/**
 * API rows → the shapes the Dispatch board draws.
 *
 * Dispatch was built against `SEED_DRIVERS` / `SEED_LOADS` and renders a richer object than
 * the database stores: a formatted quiet-hours string, a timezone abbreviation, telematics.
 * Rather than rewrite 2,145 lines of working interface to the database's field names, this
 * translates once, here, where the mismatch is visible and testable.
 *
 * ⛔ A MISSING FIELD IS A MISSING FIELD, NOT A PLAUSIBLE ONE. The seeds carried ELD positions
 * ("I-10 near Winnie, TX"), answer-rate histories and live speeds, because they were written
 * to make a screenshot look alive. None of that is in the database and none of it is invented
 * here — a dispatcher reading an invented truck position would act on it. Absent reads as
 * absent, and the board is written to show that honestly.
 */

/** "21:00" + "06:00" → "9:00p–6:00a". Null when the driver has no quiet hours set. */
function quietLabel(start, end) {
  const fmt = (hhmm) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
    if (!m) return null;
    const h = Number(m[1]);
    const suffix = h >= 12 ? 'p' : 'a';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${m[2]}${suffix}`;
  };
  const a = fmt(start);
  const b = fmt(end);
  return a && b ? `${a}–${b}` : null;
}

/** "America/Chicago" → "CT". Falls back to the raw value rather than guessing wrong. */
const TZ_ABBR = {
  'America/New_York': 'ET', 'America/Chicago': 'CT',
  'America/Denver': 'MT', 'America/Phoenix': 'MST', 'America/Los_Angeles': 'PT',
};
function tzLabel(timezone) {
  if (!timezone) return null;
  return TZ_ABBR[timezone] || String(timezone).split('/').pop().replace(/_/g, ' ');
}

export function driverFromApi(d) {
  return {
    id: d.id,
    name: d.name,
    phone: d.phone || null,
    phoneE164: d.phoneE164 || null,
    truck: d.truck || null,
    trailer: d.trailerType || null,
    language: d.language || null,
    baseLanguage: d.language || null,
    tz: tzLabel(d.timezone),
    tzShift: 0,
    quiet: quietLabel(d.quietStart, d.quietEnd),
    // ⛔ ONE FIELD MUST NOT MEAN TWO THINGS. This was `canRequest`, derived from consent,
    // and the Drivers tab put a TOGGLE on it labelled "Can give Alfred tasks" — so flipping
    // a switch about driver-initiated errands silently flipped the Call button's consent
    // gate. The names are separate now, and the consent one is read-only on that screen:
    // consent is recorded in setup, with the wording the driver agreed to, and that is the
    // legally meaningful act. A toggle is not.
    mayBeCalled: !!d.consent,
    consent: !!d.consent,
    // An AI persona's choices (backend/src/persona.mjs), or null for a person. A persona's Call
    // rings an AI on the voice platform and plays live here; it never rings a phone.
    persona: d.persona && typeof d.persona === 'object' ? d.persona : null,
    consentAt: d.consentAt || null,
    // The emergency contact IS stored and driverOut sends it; this used to drop it, while
    // the handoff panel claimed "Next rung is the emergency contact on file".
    ecName: d.ecName || null,
    ecPhone: d.ecPhone || null,
    note: d.note || '',
    // ⛔ NOT INVENTED. The seeds had attempt histories, answer times and a live ELD
    // position; the database has none of them. null is the honest answer and the board
    // renders "no data" rather than a number a dispatcher might believe.
    attempts: null,
    avgAnswerSec: null,
    eld: null,
  };
}

export function loadFromApi(l) {
  const detail = l.detail && typeof l.detail === 'object' ? l.detail : {};
  const stops = Array.isArray(l.stops) ? l.stops : [];
  return {
    id: l.id,
    ref: l.ref,
    status: l.status,
    rate: l.rate || null,
    driverId: l.driverId || null,
    // These three are jsonb columns the seeder and the API already agree on, so they come
    // through as-is: the screen's own shape was modelled on them.
    broker: l.broker || {},
    stops,
    paperwork: Array.isArray(l.paperwork) ? l.paperwork : [],
    // ⛔ THE RAW jsonb, KEPT. The board spreads `...detail` below for convenience, but a
    // screen that wants to SAVE one key of it needs the whole object to merge into — and
    // `persistPlan` was doing `{ ...(load.detail || {}) }` against a key that did not
    // exist, so its merge was always a merge onto nothing.
    detail,
    // Defaults BEFORE the spread, so a real `detail` can still carry them. The board reads
    // `load.activity.length` and `load.captured` unguarded — both were always present on
    // SEED_LOADS and are absent on every row the database returns.
    activity: [],
    captured: null,
    ...detail,
    // ⛔ PLAN LAST, AND ALWAYS AN ARRAY. The board reads `load.plan` in about fifteen
    // places and the loads endpoint has no plan column, so a real load used to crash the
    // screen on `load.plan.find(...)`. Derived from the stops, which ARE stored.
    //
    // Only the FIRST step is marked due. We do not know what the office has already done
    // on a load that is already rolling, and advancing the plan to match a status would be
    // a guess that reads as a record — a dispatcher would see "Loaded at …" ticked off for
    // a call nobody made. `detail.planOver` carries real per-step state if it is ever
    // persisted; today nothing writes it.
    plan: applyPlan(stops, (detail.planOver && typeof detail.planOver === 'object') ? detail.planOver : {})
      .map((s, i) => ({ ...s, state: s.state === 'scheduled' && i === 0 ? 'due' : s.state })),
  };
}

export function dispatcherFromApi(p) {
  return {
    id: p.id,
    name: p.name,
    role: p.role || 'Dispatch',
    phone: p.phone || null,
    hours: p.hours || null,
    onHours: p.onHours !== false,
    can: Array.isArray(p.can) ? p.can : ['ops'],
  };
}

/**
 * The per-step state worth writing back, keyed by step id.
 *
 * ⛔ THE PLAN IS DERIVED, SO ONLY THE DIFFERENCES ARE STORED. `buildPlan` rebuilds the steps
 * from the load's stops every time, which is what lets an edited stop list produce the right
 * plan with no migration. Persisting the whole rendered plan would freeze it: add a stop
 * later and the saved copy would win, so the new stop would have no calls against it and
 * nobody would be able to see why.
 *
 * So this writes back only what a person actually changed — a step marked done, a time, a
 * note — and applyPlan folds it over a freshly derived plan on the way back out. A step
 * still in its default state contributes nothing, which also keeps the jsonb small.
 */
export function planOverrides(plan) {
  const out = {};
  for (const s of plan || []) {
    if (!s || !s.id) continue;
    const diff = {};
    if (s.state && s.state !== 'scheduled') diff.state = s.state;
    if (s.at) diff.at = s.at;
    if (s.notes) diff.notes = s.notes;
    // An operator-added step has no counterpart in the derived plan, so it must carry
    // everything needed to rebuild itself.
    if (s.extra) Object.assign(diff, { extra: true, party: s.party, title: s.title, detail: s.detail });
    if (Object.keys(diff).length) out[s.id] = diff;
  }
  return out;
}

/**
 * The calls that need a person, derived from calls that actually happened.
 *
 * ⛔ THIS PANEL USED TO BE SIX INVENTED HANDOFFS. A tire blowout on I-10 with a six-field
 * intake form, a $175 lumper fee, a detention clock at Port Allen — written to make a
 * screenshot look busy, keyed to drivers nobody had hired, and shown identically to every
 * carrier. They were removed because nothing produced them.
 *
 * What DOES exist is the outcome of every call: `driver_calls` records status, the
 * platform's own refusal reason, and the transcript. Two kinds of exception fall straight
 * out of that, and both genuinely need a human:
 *
 *   · a call the platform REFUSED — quiet hours, no consent, a caller-id we do not own.
 *     Nobody was rung, and nobody finds out unless somebody looks.
 *   · a driver who has not answered repeatedly. One missed call is a driver in a yard;
 *     three is a question.
 *
 * ⛔ AND THAT IS ALL IT CLAIMS. Lumper fees, detention, appointment misses and incidents
 * need the AI to classify what was said on the call, which is not built. An empty-ish queue
 * that is true beats a full one that is decorative — the whole point of this panel is that
 * a dispatcher trusts it enough to work from it.
 */
export const UNANSWERED_BEFORE_ESCALATING = 3;

export function exceptionsFromCalls(calls, { now = new Date() } = {}) {
  const rows = (calls || []).filter(Boolean);
  // ⛔ A HANDOFF BUILT FROM A SEEDED CALL IS AN INSTRUCTION ABOUT SOMETHING THAT DID NOT
  // HAPPEN. "Alfred has stopped calling this driver. Somebody needs to reach them another
  // way" is a job for a person, and the demo seeds three no-answers precisely so the panel
  // is not empty — the seed's own comment argues that deriving it from failed calls is the
  // only honest way to fill it. So the rows stay and the MARK travels with them; `seededOf`
  // is applied to every exception this function emits, below, rather than at each site.
  const seededOf = (ids) => ids.some((id) => rows.find((r) => String(r.id) === String(id))?.seeded === true);
  const out = [];
  // ⛔ COMPARE INSTANTS, NOT STRINGS. node-postgres hands back Dates that serialize to one
  // uniform ISO form, so string order is right today — but any other serializer
  // ("2026-10-07 04:12:33+00") would sort wrong, and sorting wrong here means an
  // escalation that should have cleared stays on the board.
  const ms = (iso) => {
    const t = iso ? new Date(iso).getTime() : NaN;
    return Number.isNaN(t) ? null : t;
  };
  const minutesAgo = (iso) => {
    const t = ms(iso);
    if (t === null) return 0;
    return Math.max(0, Math.round((now.getTime() - t) / 60000));
  };

  // 1. Calls that did not happen.
  //
  // ⛔ TWO DIFFERENT PEOPLE FIX THESE, AND THE LIST HAS TO BE AN ALLOWLIST.
  //
  // This started as a denylist — `not_configured` and `unreachable`, plus anything matching
  // /^http_5/, were "ours" and everything else was read as the platform refusing the
  // driver. That is backwards. Core answers with a NAMED json error on every path including
  // its 5xx ones, so the http_5 arm only ever catches a non-json proxy page, and the twenty
  // other values core can send were all rendered as "Call refused — …" with an ask telling
  // the dispatcher the platform would not place the call. Among them: `unavailable` (core
  // could not reach its database), `call_failed` (telephony failed), `invalid_api_key` (our
  // key, and calls.mjs's own comment records a trailing newline as a lived cause),
  // `from_number_not_found` (this carrier's caller-id setup), `bad_request` / `invalid_phone`
  // / `unknown_reason` (this app sent a bad request — our bug). None of those are about the
  // driver, and sending a dispatcher to go argue with one about an outage is the specific
  // failure this split exists to prevent.
  //
  // So: only these four are a refusal ABOUT the driver or the account's calling policy.
  // Everything else — known or not yet invented — is ours until somebody adds it here.
  const POLICY_REFUSALS = new Set([
    'driver_quiet_hours',      // the driver's own quiet window
    'driver_local_hours',      // the driver's local time of day
    'quiet_hours',             // the account's quiet window
    'do_not_call',             // the driver is on the do-not-call list
  ]);
  for (const c of rows) {
    if (c.status !== 'failed' || !c.error) continue;
    const code = String(c.error);
    const why = code.replace(/_/g, ' ');
    const ourFault = !POLICY_REFUSALS.has(code);
    out.push({
      id: `refused-${c.id}`,
      kind: 'unreachable',                     // the only honest bucket NEEDS already has
      // ⛔ THE MARK TRAVELS WITH THE HANDOFF. Without it the panel tells a dispatcher to go
      // and chase a call that was never placed.
      seeded: seededOf([c.id]),
      driverId: c.driverId ?? null,
      loadRef: c.loadRef || null,
      waiting: minutesAgo(c.createdAt),
      jumped: false,
      title: code === 'no_result_from_platform' ? 'No result from the platform' : ourFault ? `Call never went out — ${why}` : `Call refused — ${why}`,
      quote: '',
      // ⛔ A call the platform never settled may have gone (backend scheduled.mjs settleOpenCalls).
      ask: code === 'no_result_from_platform'
        ? 'The platform never said how this call went, so it may have reached the driver. Check with them before calling again.'
        : code === 'not_configured'
        ? 'Calling is not connected on this server, so nothing was dialled. Nothing is wrong with the driver.'
        : ourFault
          ? `This call did not go out — the platform reported "${why}". Nothing is wrong with the driver; it can be tried again, and if it keeps happening it is ours to fix.`
          : `The platform would not place this call: ${why}. Nobody was rung.`,
    });
  }

  // 2. Drivers who are not picking up. Counted per driver, because one missed call is a
  //    driver in a yard and three is a question somebody should ask.
  //
  // ⛔ ONLY THE MISSES SINCE THEY LAST ANSWERED. Counting every no-answer ever recorded
  //    means a driver who ignored three calls last Tuesday and has answered every call
  //    since still shows as unreachable — the panel cries wolf, and a panel that cries
  //    wolf is one a dispatcher stops reading. Answering the phone clears it.
  const lastAnswered = new Map();
  for (const c of rows) {
    if (!c.driverId || c.status !== 'completed') continue;
    // ⛔ A MACHINE IS NOT AN ANSWER. calls.mjs now stamps a voicemail/IVR leg as 'no_answer',
    // but rows written before that fix are 'completed' with the subtype set, and a single
    // one of them would clear a driver's whole escalation.
    if (c.answeredSubtype === 'voicemail' || c.answeredSubtype === 'ivr') continue;
    const at = ms(c.endedAt) ?? ms(c.startedAt) ?? ms(c.createdAt);
    if (at === null || at === undefined) continue;
    if (at > (lastAnswered.get(c.driverId) ?? -Infinity)) lastAnswered.set(c.driverId, at);
  }
  const byDriver = new Map();
  for (const c of rows) {
    if (!c.driverId || c.status !== 'no_answer') continue;
    const since = lastAnswered.get(c.driverId);
    // A miss with no timestamp cannot be placed either side of the last answer. Counting it
    // would escalate a driver who has since answered, so it is left out.
    const at = ms(c.createdAt);
    if (since !== undefined && (at === null || at <= since)) continue;
    const list = byDriver.get(c.driverId) || [];
    list.push(c);
    byDriver.set(c.driverId, list);
  }
  for (const [driverId, list] of byDriver) {
    if (list.length < UNANSWERED_BEFORE_ESCALATING) continue;
    const latest = list.reduce((a, b) => ((ms(a.createdAt) ?? 0) > (ms(b.createdAt) ?? 0) ? a : b));
    out.push({
      // ⛔ THE ID CARRIES THE LATEST MISS, NOT JUST THE DRIVER. It used to be
      // `unreachable-${driverId}`, which is stable for the life of that driver — and the
      // board's "Mark handled" adds the id to a Set it never prunes. Clearing a driver's
      // escalation once meant that driver could never escalate again, however many fresh
      // calls they missed afterwards: a swallowed escalation, and a silent one. With the
      // newest missed call in the id, clearing settles THOSE misses and a later miss makes
      // a new row.
      id: `unreachable-${driverId}-${latest.id}`,
      kind: 'unreachable',
      // Seeded if ANY of the misses behind it was seeded: the escalation exists because of
      // all of them together, so one example in the chain makes the whole row an example.
      seeded: seededOf(list.map((x) => x.id)),
      driverId,
      loadRef: latest.loadRef || null,
      waiting: minutesAgo(latest.createdAt),
      jumped: false,
      title: `${list.length} calls, no answer`,
      quote: '',
      // ⛔ THIS SAID "Next rung is the emergency contact on file." There is no next rung —
      // nothing in this app dials anybody but the driver — and it was not read from the
      // driver's record either, so it printed identically for a driver with no emergency
      // contact stored. It says what is true: somebody has to pick up the phone.
      ask: 'Alfred has stopped calling this driver. Somebody needs to reach them another way.',
    });
  }

  // Longest-waiting first: the thing nobody has picked up is the thing to show first.
  return out.sort((a, b) => b.waiting - a.waiting);
}

export const driversFromApi = (rows) => (rows || []).map(driverFromApi);
export const loadsFromApi = (rows) => (rows || []).map(loadFromApi);
export const dispatchersFromApi = (rows) => (rows || []).map(dispatcherFromApi);

/* ------------------------------- call plan --------------------------------- */

/**
 * ⛔ MOVED HERE FROM Dispatch.jsx, BECAUSE THE API NEVER SENDS A PLAN.
 *
 * `load.plan` is read in about fifteen places on the board — the step list, the "due now"
 * count, the next-due label, the call button. Every one of them was written against
 * SEED_LOADS, which carried a hand-written `plan` array. The loads endpoint has no such
 * column: it stores ref, status, rate, broker, stops, paperwork and a free-form `detail`
 * jsonb. So the first real load to reach this board hit `load.plan.find(...)` on undefined
 * and took the screen down.
 *
 * The plan is DERIVED from the stops, which the database does store. That is also the
 * honest model: a 2-pick 1-drop load genuinely has a different call plan from a 1-pick
 * 1-drop, and deriving it means an imported load gets the right one without anybody
 * writing it down.
 *
 * ⚠️ STEP STATE IS NOT PERSISTED YET. Marking a step done lives in React state and is lost
 * on reload — nothing writes it back. That is a real gap, not a hidden one; it is called
 * out here rather than papered over with a plan that looks saved.
 */

/**
 * Builds the plan from the stop list, so a 2-pick 1-drop load gets
 * arrive/loaded steps per pickup rather than one generic "at the shipper".
 */
export function buildPlan(stops = []) {
  const p = [
    { id: "assign", party: "driver", title: "Load assignment", detail: "Brief the driver on the load, confirm they accept it, read back the first pickup address and appointment." },
    { id: "truckinfo", party: "broker", title: "Truck and driver details", detail: "Truck number, trailer, driver name and cell." },
    { id: "yard", party: "driver", title: "Left the yard?", detail: "Confirm the driver is rolling toward the first stop and get an ETA." },
    { id: "etapickup", party: "broker", title: "Departed for pickup", detail: "Departure time and ETA to the first shipper." },
  ];
  const pickups = stops.filter((s) => s.type === "pickup");
  const drops = stops.filter((s) => s.type === "drop");

  stops.forEach((s) => {
    if (s.type === "pickup") {
      const nth = pickups.length > 1 ? ` (pickup ${pickups.indexOf(s) + 1} of ${pickups.length})` : "";
      p.push({ id: `arrive${s.seq}`, stopSeq: s.seq, party: "driver", title: `Arrived at ${s.name}${nth}`, detail: `Confirm gate-in at ${s.name}, start the detention clock, ask about dock delay.`, captures: ["Gate-in time", "Dock"] });
      p.push({ id: `loaded${s.seq}`, stopSeq: s.seq, party: "broker", title: `Loaded at ${s.name}`, detail: "Read back BOL and seal numbers and weight, then pass them to the broker with an ETA to the next stop.", captures: ["BOL number", "Seal number", "Weight"] });
    }
  });

  p.push({ id: "enroute", party: "driver", title: "En route check call", detail: "Position, hours remaining, anything needed." });

  stops.forEach((s) => {
    if (s.type === "drop") {
      const nth = drops.length > 1 ? ` (drop ${drops.indexOf(s) + 1} of ${drops.length})` : "";
      p.push({ id: `arrive${s.seq}`, stopSeq: s.seq, party: "driver", title: `Arrived at ${s.name}${nth}`, detail: `Confirm arrival and unload status at ${s.name}. Start the detention clock if they're held.`, captures: ["Gate-in time"] });
      p.push({ id: `pod${s.seq}`, stopSeq: s.seq, party: "driver", title: `Unloaded at ${s.name}`, detail: "Confirm who signed the POD and at what time, and whether anything was noted on it.", captures: ["POD signer", "POD time", "Exceptions noted"] });
    }
  });

  p.push({ id: "delivered", party: "broker", title: "Delivered", detail: "POD details, detention, and empty status." });
  p.push({ id: "empty", party: "driver", title: "Empty and available", detail: "Confirm the driver is empty, where they're sitting, and what hours they have left for a reload.", captures: ["Empty location", "Hours available"] });

  return p.map((s) => ({ ...s, at: "", state: "scheduled", notes: "", extra: false, captures: s.captures || [] }));
}

export const applyPlan = (stops, over) =>
  buildPlan(stops).map((s) => ({ ...s, ...(over[s.id] || {}) }));


/* ------------------------------- calls ------------------------------------- */

/**
 * The platform's transcript is ONE STRING, not a list of turns.
 *
 * ⛔ THE BOARD USED TO RENDER `c.transcript.map(...)` over a hand-written array of
 * `{who, text}` objects. The real column is a newline-joined string the box assembles in
 * `cmem.assemble_transcript`, shaped `Caller: …` / `AI: …` (core's own
 * `hadRealContactToday()` counts the `Caller:` lines, so the prefixes are load-bearing and
 * stable). Handing that string to `.map` is a crash, which is the good outcome; the bad one
 * is a screen that silently shows nothing where a driver's words should be.
 *
 * A line we cannot attribute keeps its text and gets NO speaker. Guessing "alfred" for an
 * unprefixed line would put words in Alfred's mouth in the record of a real conversation.
 */
export function parseTranscript(text) {
  const raw = String(text || '').trim();
  if (!raw) return [];
  return raw.split(/\r?\n/).reduce((out, line) => {
    const s = line.trim();
    if (!s) return out;
    const m = /^(AI|Alfred|Caller|Driver|Agent|User)\s*:\s*(.*)$/i.exec(s);
    if (!m) {
      // A continuation of the previous turn (the box wraps long turns) reads as the same
      // speaker. A leading orphan belongs to nobody.
      const prev = out[out.length - 1];
      if (prev) prev.text += ` ${s}`;
      else out.push({ who: null, speaker: null, text: s });
      return out;
    }
    const who = /^(AI|Alfred|Agent)$/i.test(m[1]) ? 'alfred' : 'driver';
    let body = m[2];
    // Panel mode prefixes the persona: `AI: [Megan] …`. Keep the name rather than drop it.
    let speaker = null;
    const named = /^\[([^\]]{1,40})\]\s*(.*)$/.exec(body);
    if (named) { speaker = named[1]; body = named[2]; }
    out.push({ who, speaker, text: body });
    return out;
  }, []);
}

/** What the office calls each reason. The API's enum is SHOUTED; a screen should not be. */
const REASON_LABEL = {
  LOAD_OFFER: 'Load offer', CHECK_IN: 'Check call', ETA_STATUS: 'ETA and status',
  PICKUP_CONFIRM: 'Pickup confirmation', DELIVERY_CONFIRM: 'Delivery confirmation', ISSUE: 'Issue',
};

/**
 * The status the dispatcher reads. `failed` carries the platform's own reason, because
 * "Failed" alone sends someone to the logs for something the row already knows.
 */
const STATUS_LABEL = {
  queued: 'Queued', placed: 'Placed', completed: 'Completed', no_answer: 'No answer', failed: 'Failed',
};

/** A clock label in the viewer's zone. The API sends ISO; the board shows "10:06 AM". */
function atLabel(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** today / yesterday / the date — what the History filter segments on. */
function dayBucket(iso, now = new Date()) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const midnight = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((midnight(now) - midnight(d)) / 86400000);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * One `driver_calls` row → the row the History tab draws.
 *
 * ⛔ THREE FIELDS THE SEEDS HAD AND THE DATABASE DOES NOT, left null on purpose:
 *   · `recordingUrl` — there is no recording endpoint for a freight leg. The seed drew a
 *     waveform from `parseInt(c.id.slice(1))` and animated a Play button over it. That is a
 *     control that promises audio nobody can produce; the board hides it when this is null.
 *   · `captured` — the structured "BOL GC-77413 / Seal 88214" chips were written by hand.
 *     Nothing extracts fields from a freight transcript yet.
 *   · `dur` — derived from startedAt/endedAt when both exist, else null. The row is written
 *     before the call is placed, so a queued call has no duration and must not show "0:00".
 *
 * `loads` is passed so a loadId becomes the ref a dispatcher recognises; without a match the
 * ref is null rather than a made-up "HOU-…".
 */
export function callFromApi(c, loads = []) {
  const load = c.loadId ? (loads || []).find((l) => l.id === c.loadId) : null;
  const started = c.startedAt ? new Date(c.startedAt) : null;
  const ended = c.endedAt ? new Date(c.endedAt) : null;
  const dur = started && ended && !Number.isNaN(started) && !Number.isNaN(ended)
    ? Math.max(0, Math.round((ended - started) / 1000))
    : null;
  return {
    id: String(c.id),
    driverId: c.driverId || null,
    loadId: c.loadId || null,
    // 'voicemail' | 'ivr' when the box determined no live person came on the line; null when
    // a person was reached OR the box could not tell. Absent is not "machine".
    answeredSubtype: c.answeredSubtype || null,
    loadRef: load ? load.ref : null,
    party: 'driver',
    step: REASON_LABEL[c.reason] || c.reason || 'Call',
    reason: c.reason || null,
    status: c.status || null,
    // The label the row shows. A failure says what failed.
    // ⛔ Closed with no word from the platform (backend scheduled.mjs): it may have gone.
    outcome: c.status === 'failed' && c.error === 'no_result_from_platform'
      ? 'No result from the platform · may have gone through'
      : c.status === 'failed' && c.error
      ? `Failed · ${String(c.error).replace(/_/g, ' ')}`
      // A voicemail is not "No answer" and it is certainly not "Completed" — the office
      // wants to know Alfred left a message rather than wonder whether anyone picked up.
      : c.answeredSubtype === 'voicemail' ? 'Voicemail'
      : c.answeredSubtype === 'ivr' ? 'Phone menu, no person'
      : (STATUS_LABEL[c.status] || c.status || 'Unknown'),
    error: c.error || null,
    at: atLabel(c.startedAt || c.createdAt),
    day: dayBucket(c.startedAt || c.createdAt),
    createdAt: c.createdAt || null,
    startedAt: c.startedAt || null,
    endedAt: c.endedAt || null,
    dur,
    transcript: parseTranscript(c.transcript),
    hasTranscript: !!String(c.transcript || '').trim(),
    coreCallId: c.coreCallId || null,
    requestedBy: c.requestedBy || null,
    // ⛔ A SEEDED CALL IS NOT A CALL. `driver_calls` rows are the one thing on this board that
    // assert an event occurred, so a row written for a demonstration has to carry that through
    // to the screen — `=== true` so a row from before the column existed reads as a real call
    // rather than as an unmarked example.
    seeded: c.seeded === true,
    // The far end was an AI persona (a demo driver on a 555-01xx number), not a person. Shown on
    // the row so a demonstration call never reads as a call to somebody's phone.
    persona: c.persona === true,
    // ⛔ see the note above — absent, not invented.
    recordingUrl: null,
    captured: [],
  };
}

export const callsFromApi = (rows, loads = []) => (rows || []).map((c) => callFromApi(c, loads));
